import { EventType } from "@/analytics/analytics-events";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppDispatch } from "@/store/store-hooks";
import { Tag } from "@/store/tags/tags-slice";
import { setShowSpinner } from "@/store/ui/ui-slice";
import {
  FaroChipList,
  FaroChipListProps,
  FaroChipTag,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID } from "@faro-lotv/foundation";
import { IElement, ILabel } from "@faro-lotv/ielement-types";
import { SxProps } from "@mui/material/styles";
import { useCallback, useMemo, useState, type JSX } from "react";
import { isTagReadable, isTagWritable } from "./tag-accessibility";
import { useRemoveTagFromElement } from "./use-remove-tag-from-element";

type ElementTagsChipListProps = Pick<
  ElementTagsChipListBaseProps,
  "reverse" | "dark" | "popperZIndex" | "disabled" | "sx"
> & {
  /** The element to show the tags for */
  element: IElement;
};

/** @returns A ChipList showing the tags of an element, with the ability to quickly remove them. */
export function ElementTagsChipList({
  element,
  ...baseProps
}: ElementTagsChipListProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorHandlers();

  const removeTag = useRemoveTagFromElement(element);
  const onRemoveTagFromElement = useCallback(
    async (label: ILabel) => {
      Analytics.track(EventType.removeTagFromScan);
      dispatch(setShowSpinner(true));
      try {
        await removeTag(label);
      } catch (error) {
        handleErrorWithToast({
          title: "Could not remove tag",
          error,
        });
      } finally {
        dispatch(setShowSpinner(false));
      }
    },
    [dispatch, handleErrorWithToast, removeTag],
  );

  return (
    <ElementTagsChipListBase
      tags={element.labels ?? undefined}
      onRemoveTagFromElement={onRemoveTagFromElement}
      {...baseProps}
    />
  );
}

type ElementTagsChipListBaseProps = Pick<
  FaroChipListProps,
  "reverse" | "dark" | "popperZIndex"
> & {
  /** The element to show the tags for */
  tags?: Tag[];

  /** Whether to disable interactions with the ChipList */
  disabled?: boolean;

  /** Styles to apply to the ChipList */
  sx?: SxProps;

  onRemoveTagFromElement?(tag: Tag): Promise<void> | void;
};

/**
 * @returns A chip list input component for element tags, without any mutation logic.
 */
export function ElementTagsChipListBase({
  tags,
  disabled,
  sx,
  onRemoveTagFromElement,
  ...chipListProps
}: ElementTagsChipListBaseProps): JSX.Element {
  const [tagRemoved, setTagRemoved] = useState<GUID>();

  const readableElementTags = useReadableElementTags(tags);

  const chips = useMemo(
    () =>
      readableElementTags?.map((label) => (
        <FaroChipTag
          label={label.name}
          key={label.id}
          showSpinner={tagRemoved === label.id}
          disabled={!!tagRemoved || disabled}
          onDelete={
            isTagWritable(label)
              ? async () => {
                  setTagRemoved(label.id);
                  try {
                    await onRemoveTagFromElement?.(label);
                  } finally {
                    setTagRemoved(undefined);
                  }
                }
              : undefined
          }
          dark={chipListProps.dark}
        />
      )) ?? [],
    [
      chipListProps.dark,
      disabled,
      onRemoveTagFromElement,
      readableElementTags,
      tagRemoved,
    ],
  );

  return (
    <FaroChipList
      chips={chips}
      sx={[
        { minWidth: chips.length ? "40px" : 0 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      showSingleChip={false}
      {...chipListProps}
    />
  );
}

/**
 * @returns The list of readable element tags, that can be shown to a user.
 * @param tags The element to get the tags for.
 */
export function useReadableElementTags(tags?: Tag[]): Tag[] | undefined {
  return useMemo(() => tags?.filter(isTagReadable), [tags]);
}
